import type { Alpha3Code } from "i18n-iso-countries/index"

export type Link = {
  href: string
  text: string
}

export function linkify(
  { href, text }: Link,
  cssClass: string = "text-general-hyperlink"
) {
  return `<a class="${cssClass}" rel="noopener noreferrer" target="_blank" href="${href}">${text}</a>`
}

export function mailtoLink(email: string, cssClass?: string) {
  return linkify(
    {
      text: email,
      href: `mailto:${email}`,
    },
    cssClass
  )
}

export function learnMoreLink(href: string, cssClass?: string) {
  return linkify({ text: "Learn more", href }, cssClass)
}

export const SUPPORT_EMAIL = "support@tastytrade.com"
export const MAIL_TO_SUPPORT = `mailto:${SUPPORT_EMAIL}`
export const MAIL_TO_SUPPORT_LINK = mailtoLink(SUPPORT_EMAIL)
export const BANKING_EMAIL = "banking@tastytrade.com"
export const MAIL_TO_BANKING = `mailto:${BANKING_EMAIL}`
export const MAIL_TO_BANKING_LINK = mailtoLink(BANKING_EMAIL)
export const ACCOUNTS_EMAIL = "accounts@tastytrade.com"
export const MAIL_TO_ACCOUNTS = `mailto:${ACCOUNTS_EMAIL}`
export const MAIL_TO_ACCOUNTS_LINK = mailtoLink(ACCOUNTS_EMAIL)

export const SUPPORT_CONTACT_PAGE =
  "https://support.tastytrade.com/support/s/solutions/articles/43000476860"

export const SUPPORT_PHONE_NUMBER = `+13127247075`
export const SUPPORT_PHONE_NUMBER_LINK = `<a class="text-general-hyperlink" href="tel:${SUPPORT_PHONE_NUMBER}">(312) 724-7075</a>`

export const PASSKEYS_HELP_CENTER = "https://support.tastytrade.com/support/s/"

export const RTP_HELP_CENTER =
  "https://support.tastytrade.com/support/s/solutions/articles/Real-Time-Payments"
export const RTP_HELP_CENTER_LINK = learnMoreLink(RTP_HELP_CENTER)

export const TAX_WITHHOLDINGS_HELP_CENTER =
  "https://support.tastytrade.com/support/s/solutions/articles/43000472762"
export const TAX_WITHHOLDINGS_HELP_CENTER_LINK = `<a rel="noopener noreferrer" target="_blank" href="${TAX_WITHHOLDINGS_HELP_CENTER}">Learn more</a>`
export const RISK_DISCLOSURE_URL =
  "https://zerohash.zendesk.com/hc/en-us/articles/28327983001235-General-Risks-of-Digital-Assets"
export const IRA_CONTRIBUTION_LIMITS_HELP_CENTER =
  "https://support.tastytrade.com/support/s/solutions/articles/43000435268"

export const WEB_PLATFORM_SOURCE_IDENTIFIER = "WB2"
export const ELECTRON_PLATFORM_SOURCE_IDENTIFIER = "ELECTRON"

export const LOGIN_MESSAGE_KEY = "login-message"

export enum ACCOUNT_TYPES {
  BENEFICIARY_ROTH_IRA = "Beneficiary Roth IRA",
  BENEFICIARY_TRADITIONAL_IRA = "Beneficiary Traditional IRA",
  INDIVIDUAL = "Individual",
  JOINT_TENANTS_IN_COMMON = "Joint Tenants in Common",
  JOINT_WROS = "Joint Tenants with Rights of Survivorship",
  ROTH_IRA = "Roth IRA",
  SEP_IRA = "SEP",
  TRADITIONAL_IRA = "Traditional IRA",

  BUSINESS = "Business",
  C_CORP = "C. Corp",
  S_CORP = "S. Corp",
  LLC = "LLC",
  PARTNERSHIP = "Partnership",
  TRUST = "Trust",
}

export enum ENTITY_ACCOUNT_TYPE_CHOICES {
  C_CORP = "cCorp",
  S_CORP = "sCorp",
  LLC = "llc",
  PARTNERSHIP = "partnership",
  TRUST = "trust",
}

export enum ALLOCATION_STYLES {
  PER_TRADE = "Per Trade",
  UNIT = "Unit",
}

export enum ALLOCATION_METHODS {
  BUYING_POWER_PERCENTAGE = "Buying Power Percentage",
  CONTRACT_QUANTITY = "Contract Quantity",
  DOLLAR_AMOUNT = "Dollar Amount",
  UNIT_COUNT = "Unit Count",
}

export enum WITHHOLDING_TYPES {
  PERCENT = "PERCENTAGE",
  FIXED = "FIXED",
}

export enum CHECK_DELIVERY_METHODS {
  OVERNIGHT = "Overnight",
  STANDARD = "Standard",
}

export enum COMMUNICATION_GROUP {
  /** @deprecated */
  ACCOUNT = "Account Communications",
  /** @deprecated */
  TRADE = "Trade Communications",
  /** @deprecated */
  MARKETING = "Marketing Communications",
  /** @deprecated */
  REFERRAL = "Referral Communications",
  /** @deprecated */
  CASH = "Cash Communications",
  /** @deprecated */
  ORDER_FILL_NOTIFICATIONS = "Order Fill Notifications",

  // Reworked Communication Groups
  SECURITY = "Security",
  ACCOUNT_MANAGEMENT = "Account Management",
  ANNOUNCEMENTS = "Announcements",
  MARKET_NEWS = "Market News",
  PROMOTIONS = "Promotions",
  EDUCATION = "Education",
  FEEDBACK = "Feedback",
  MARGIN_AND_RISK = "Margin & Risk",
  ORDER_FILLS = "Order Fills",
  POSITION_ALERTS = "Position Alerts",
  PRICE_ALERTS = "Price Alerts",
  // only used for initialization
  BLANK = "",
}

export enum CUSTOMER_ACCOUNT_AUTHORITY_LEVELS {
  OWNER = "owner",
  READ_ONLY = "read-only",
  TRADE_ONLY = "trade-only",
}

export enum DIGITAL_ASSET_DEPOSIT_STATUSES {
  NEEDS_MANUAL_RETURN = "Needs Manual Return",
  RETURN_SUBMITTED_TO_CLEARING = "Return Submitted to Clearing",
  RETURNED = "Returned",
  SETTLED = "Settled",
  UNDER_REVIEW = "Under Review",
}

export enum DIGITAL_ASSET_WITHDRAWAL_STATUSES {
  AWAITING_EXECUTION = "Awaiting Execution",
  EXPIRED = "Expired",
  CLEARING_APPROVED = "Clearing Approved",
  CLEARING_PROCESSED = "Clearing Processed",
  CLEARING_REJECTED = "Clearing Rejected",
  CLEARING_SETTLED = "Clearing Settled",
  SUBMITTED_TO_CLEARING = "Submitted to Clearing",
  UNDER_REVIEW = "Under Review",
  CANCELED = "Canceled",
}
export const EN_COUNTRY_LABELS = {
  ABW: "Aruba",
  AFG: "Afghanistan",
  AGO: "Angola",
  AIA: "Anguilla",
  ALA: "Åland Islands",
  ALB: "Albania",
  AND: "Andorra",
  ARE: "United Arab Emirates",
  ARG: "Argentina",
  ARM: "Armenia",
  ASM: "American Samoa",
  ATA: "Antarctica",
  ATF: "French Southern Territories",
  ATG: "Antigua and Barbuda",
  AUS: "Australia",
  AUT: "Austria",
  AZE: "Azerbaijan",
  BDI: "Burundi",
  BEL: "Belgium",
  BEN: "Benin",
  BES: "Bonaire (Sint Eustatius and Saba)",
  BFA: "Burkina Faso",
  BGD: "Bangladesh",
  BGR: "Bulgaria",
  BHR: "Bahrain",
  BHS: "Bahamas",
  BIH: "Bosnia and Herzegovina",
  BLM: "Saint Barthélemy",
  BLR: "Belarus",
  BLZ: "Belize",
  BMU: "Bermuda",
  BOL: "Bolivia (Plurinational State of)",
  BRA: "Brazil",
  BRB: "Barbados",
  BRN: "Brunei Darussalam",
  BTN: "Bhutan",
  BVT: "Bouvet Island",
  BWA: "Botswana",
  CAF: "Central African Republic",
  CAN: "Canada",
  CCK: "Cocos (Keeling) Islands",
  CHE: "Switzerland",
  CHL: "Chile",
  CHN: "China",
  CIV: "Côte d'Ivoire",
  CMR: "Cameroon",
  COD: "Congo (Democratic Republic of the)",
  COG: "Congo",
  COK: "Cook Islands",
  COL: "Colombia",
  COM: "Comoros",
  CPV: "Cabo Verde",
  CRI: "Costa Rica",
  CUB: "Cuba",
  CUW: "Curaçao",
  CXR: "Christmas Island",
  CYM: "Cayman Islands",
  CYP: "Cyprus",
  CZE: "Czech Republic",
  DEU: "Germany",
  DJI: "Djibouti",
  DMA: "Dominica",
  DNK: "Denmark",
  DOM: "Dominican Republic",
  DZA: "Algeria",
  ECU: "Ecuador",
  EGY: "Egypt",
  ERI: "Eritrea",
  ESH: "Western Sahara",
  ESP: "Spain",
  EST: "Estonia",
  ETH: "Ethiopia",
  FIN: "Finland",
  FJI: "Fiji",
  FLK: "Falkland Islands (Malvinas)",
  FRA: "France",
  FRO: "Faroe Islands",
  FSM: "Micronesia (Federated States of)",
  GAB: "Gabon",
  GBR: "United Kingdom of Great Britain and Northern Ireland",
  GEO: "Georgia",
  GGY: "Guernsey",
  GHA: "Ghana",
  GIB: "Gibraltar",
  GIN: "Guinea",
  GLP: "Guadeloupe",
  GMB: "Gambia",
  GNB: "Guinea-Bissau",
  GNQ: "Equatorial Guinea",
  GRC: "Greece",
  GRD: "Grenada",
  GRL: "Greenland",
  GTM: "Guatemala",
  GUF: "French Guiana",
  GUM: "Guam",
  GUY: "Guyana",
  HKG: "Hong Kong",
  HMD: "Heard Island and McDonald Islands",
  HND: "Honduras",
  HRV: "Croatia",
  HTI: "Haiti",
  HUN: "Hungary",
  IDN: "Indonesia",
  IMN: "Isle of Man",
  IND: "India",
  IOT: "British Indian Ocean Territory",
  IRL: "Ireland",
  IRN: "Iran (Islamic Republic of)",
  IRQ: "Iraq",
  ISL: "Iceland",
  ISR: "Israel",
  ITA: "Italy",
  JAM: "Jamaica",
  JEY: "Jersey",
  JOR: "Jordan",
  JPN: "Japan",
  KAZ: "Kazakhstan",
  KEN: "Kenya",
  KGZ: "Kyrgyzstan",
  KHM: "Cambodia",
  KIR: "Kiribati",
  KNA: "Saint Kitts and Nevis",
  KOR: "Korea (Republic of)",
  KWT: "Kuwait",
  LAO: "Lao People's Democratic Republic",
  LBN: "Lebanon",
  LBR: "Liberia",
  LBY: "Libya",
  LCA: "Saint Lucia",
  LIE: "Liechtenstein",
  LKA: "Sri Lanka",
  LSO: "Lesotho",
  LTU: "Lithuania",
  LUX: "Luxembourg",
  LVA: "Latvia",
  MAC: "Macao",
  MAF: "Saint Martin (French part)",
  MAR: "Morocco",
  MCO: "Monaco",
  MDA: "Moldova (Republic of)",
  MDG: "Madagascar",
  MDV: "Maldives",
  MEX: "Mexico",
  MHL: "Marshall Islands",
  MKD: "Macedonia (the former Yugoslav Republic of)",
  MLI: "Mali",
  MLT: "Malta",
  MMR: "Myanmar",
  MNE: "Montenegro",
  MNG: "Mongolia",
  MNP: "Northern Mariana Islands",
  MOZ: "Mozambique",
  MRT: "Mauritania",
  MSR: "Montserrat",
  MTQ: "Martinique",
  MUS: "Mauritius",
  MWI: "Malawi",
  MYS: "Malaysia",
  MYT: "Mayotte",
  NAM: "Namibia",
  NCL: "New Caledonia",
  NER: "Niger",
  NFK: "Norfolk Island",
  NGA: "Nigeria",
  NIC: "Nicaragua",
  NIU: "Niue",
  NLD: "Netherlands",
  NOR: "Norway",
  NPL: "Nepal",
  NRU: "Nauru",
  NZL: "New Zealand",
  OMN: "Oman",
  PAK: "Pakistan",
  PAN: "Panama",
  PCN: "Pitcairn",
  PER: "Peru",
  PHL: "Philippines",
  PLW: "Palau",
  PNG: "Papua New Guinea",
  POL: "Poland",
  PRK: "Korea (Democratic People's Republic of)",
  PRT: "Portugal",
  PRY: "Paraguay",
  PSE: "Palestine (State of)",
  PYF: "French Polynesia",
  QAT: "Qatar",
  REU: "Réunion",
  ROU: "Romania",
  RUS: "Russian Federation",
  RWA: "Rwanda",
  SAU: "Saudi Arabia",
  SDN: "Sudan",
  SEN: "Senegal",
  SGP: "Singapore",
  SGS: "South Georgia and the South Sandwich Islands",
  SHN: "Saint Helena (Ascension and Tristan da Cunha)",
  SJM: "Svalbard and Jan Mayen",
  SLB: "Solomon Islands",
  SLE: "Sierra Leone",
  SLV: "El Salvador",
  SMR: "San Marino",
  SOM: "Somalia",
  SPM: "Saint Pierre and Miquelon",
  SRB: "Serbia",
  SSD: "South Sudan",
  STP: "Sao Tome and Principe",
  SUR: "Suriname",
  SVK: "Slovakia",
  SVN: "Slovenia",
  SWE: "Sweden",
  SWZ: "Swaziland",
  SXM: "Sint Maarten (Dutch part)",
  SYC: "Seychelles",
  SYR: "Syrian Arab Republic",
  TCA: "Turks and Caicos Islands",
  TCD: "Chad",
  TGO: "Togo",
  THA: "Thailand",
  TJK: "Tajikistan",
  TKL: "Tokelau",
  TKM: "Turkmenistan",
  TLS: "Timor-Leste",
  TON: "Tonga",
  TTO: "Trinidad and Tobago",
  TUN: "Tunisia",
  TUR: "Turkey",
  TUV: "Tuvalu",
  TWN: "Taiwan",
  TZA: "Tanzania (United Republic of)",
  UGA: "Uganda",
  UKR: "Ukraine",
  UMI: "United States Minor Outlying Islands",
  URY: "Uruguay",
  USA: "United States of America",
  UZB: "Uzbekistan",
  VAT: "Holy See",
  VCT: "Saint Vincent and the Grenadines",
  VEN: "Venezuela (Bolivarian Republic of)",
  VGB: "Virgin Islands (British)",
  VIR: "Virgin Islands (U.S.)",
  VNM: "Vietnam",
  VUT: "Vanuatu",
  WLF: "Wallis and Futuna",
  WSM: "Samoa",
  YEM: "Yemen",
  ZAF: "South Africa",
  ZMB: "Zambia",
  ZWE: "Zimbabwe",
} as Record<Alpha3Code, string>

export const US_STATE_OPTIONS = [
  "AA",
  "AE",
  "AK",
  "AL",
  "AP",
  "AR",
  "AZ",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "GU",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
] as string[]

/**
 * the ineligibility statuses will show with the following priority:
 * 1. INELIGIBLE_REGION
 * 2. INELIGIBLE_ACCOUNT_TYPE
 * 3. INSUFFICIENT_BALANCE
 * 4. INELIGIBLE_SUITABILITY
 */
export enum FULLY_PAID_SECURITIES_LENDING_ELIGIBILITY_STATUS {
  ELIGIBLE = "ELIGIBLE",
  INELIGIBLE_REGION = "INELIGIBLE_REGION",
  INELIGIBLE_ACCOUNT_TYPE = "INELIGIBLE_ACCOUNT_TYPE",
  INSUFFICIENT_BALANCE = "INSUFFICIENT_BALANCE",
  INELIGIBLE_SUITABILITY = "INELIGIBLE_SUITABILITY",
}
export enum INVESTMENT_EXPERIENCES {
  LIMITED = "LIMITED",
  GOOD = "GOOD",
  EXTENSIVE = "EXTENSIVE",
}
export enum IRA_DISTRIBUTION_REASONS {
  CONVERSION = "CONVERSION",
  DEATH = "DEATH",
  EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE = "EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE",
  EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE = "EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE",
  NORMAL = "NORMAL",
  PREMATURE = "PREMATURE",
  RECHARACTERIZATION_PRIOR_YEAR = "RECHARACTERIZATION_PRIOR_YEAR",
  RECHARACTERIZATION_CURRENT_YEAR = "RECHARACTERIZATION_CURRENT_YEAR",
  TRANSFER = "TRANSFER",
}
export enum IRA_CONTRIBUTION_REASONS {
  CONVERSION = "CONVERSION",
  EMPLOYEE = "EMPLOYEE",
  EMPLOYER = "EMPLOYER",
  RECHARACTERIZATION = "RECHARACTERIZATION",
  REGULAR = "REGULAR",
  ROLLOVER_60_DAY = "ROLLOVER_60_DAY",
  ROLLOVER_DIRECT = "ROLLOVER_DIRECT",
  TRANSFER = "TRANSFER",
}
export enum LIQUIDITY_NEEDS {
  VERY_IMPORTANT = "VERY_IMPORTANT",
  SOMEWHAT_IMPORTANT = "SOMEWHAT_IMPORTANT",
  NOT_IMPORTANT = "NOT_IMPORTANT",
}
export enum RECURRING_DEPOSIT_STATUSES {
  ACTIVE = "Active",
  CANCELED = "Canceled",
  COMPLETE = "Complete",
  PRE_SEND = "Pre-Send",
  SEND_FAILED = "Send Failed",
}
export enum TRADING_REQUEST_TYPES {
  CRYPTOCURRENCY_TRADING = "Cryptocurrency Trading",
  EQUITY_OFFERING = "Equity Offering",
  INTRADAY_FUTURES_MARGIN = "Intraday Futures Margin",
  IRA_SHORT_CALL = "IRA Short Call",
  PORTFOLIO_MARGIN = "Portfolio Margin",
}
// Trading preferences that are not backed by a TRADING_REQUEST_TYPE
export enum ADDITIONAL_TRADING_PREFERENCES {
  FUTURES_TRADING = "Futures Trading",
  EVENT_CONTRACTS = "Event Contracts",
}
export enum TRANSFER_METHODS {
  BLANK = "",
  ACH = "ACH",
  RTP = "RTP",
  WIRE = "WIRE",
  CHECK = "CHECK",
  STABLECOIN = "STABLECOIN",
}
export enum TAX_DOCUMENT_SLUGS {
  IRS_1042_S_TAX = "irs_1042_s_tax",
  IRS_1099_TAX = "irs_1099_tax",
  IRS_1099B_CRYPTOCURRENCIES_TAX = "irs_1099b_cryptocurrencies_tax",
  IRS_1099B_TAX = "irs_1099b_tax",
  IRS_1099R_TAX = "irs_1099r_tax",
  IRS_5498_TAX = "irs_5498_tax",
  IRS_1099DA_TAX = "irs_1099da_tax",
}
export enum TAX_WORKSHEET_SLUGS {
  GAIN_LOSS_TAX_WORKSHEET = "gain_loss_tax_worksheet",
  NO_WASH_SALES_TAX_WORKSHEET = "no_wash_sales_tax_worksheet",
  WITH_WASH_SALES_TAX_WORKSHEET = "with_wash_sales_tax_worksheet",
}
export enum TRANSFER_TYPE {
  PARTIAL = "PARTIAL_TRANSFER_RECEIVER",
  FULL = "FULL_TRANSFER",
}
export enum TWO_FACTOR_TYPE {
  AUTHENTICATOR = "authenticator",
  EMAIL = "email",
  SMS = "sms",
}

export enum USER_TEAM_ROLE {
  OWNER = "owner",
  MEMBER = "member",
}
